/**
 * Session notification history — the framework-agnostic half.
 *
 * Every session-related PUSH (member journey pushes, follower fan-out,
 * provider reminders/alerts) also leaves one row in the user's in-app
 * `notifications` table so a tapped/dismissed push never "disappears". This
 * module owns the vocabulary both sides share: which push `data.type`s are
 * recorded, the stable `subtype` slug stored on the row, the in-app deep link,
 * and the one-shot "occurrence" used to keep the write idempotent. The lambda
 * writer (`infrastructure/lambda/shared/session-notification-history.ts`) does
 * the DynamoDB work; the frontend feed reads `subtype` for the row icon.
 *
 * PRIVACY: a history row carries ONLY the push's own title/body — first names,
 * no address, no coordinates, no notes — plus the session uuid. Followers
 * therefore get exactly the privacy of their push.
 */

/** Row `type` discriminator in the notifications table / GraphQL `Notification.type`. */
export const SESSION_NOTIFICATION_TYPE = 'SESSION'

/** How long history rows live (matches the notifications table TTL policy). */
export const SESSION_NOTIFICATION_TTL_DAYS = 30

export type SessionNotificationSubtype =
  | 'pre_shift_eta'
  | 'prep_travel'
  | 'leave_now'
  | 'en_route'
  | 'nearby'
  | 'arrived'
  | 'arrived_late'
  | 'arrived_check_in'
  | 'checked_out'
  | 'session_started'
  | 'session_ended'
  | 'cancelled'
  | 'cancel_request'
  | 'reminder_start'
  | 'reminder_prep'
  | 'reminder_end'
  | 'not_started'
  | 'callback_booked'
  | 'recording_consent_request'

/**
 * Push `data.type` → history subtype. `PROVIDER_ARRIVED` is refined to
 * `arrived_late` by {@link sessionNotificationSubtype} when `data.late` is set.
 *
 * `SESSION_CANCEL_REQUEST` is deliberately ABSENT: the cancel-request bell row
 * is already written by `putProviderNotification` (type `SESSION_CANCEL_REQUEST`),
 * so recording it here too would duplicate it. The feed maps that legacy type
 * to the same `cancel_request` subtype ({@link subtypeForNotificationType}).
 */
const SUBTYPE_BY_PUSH_TYPE: Readonly<Record<string, SessionNotificationSubtype>> = {
  PRE_SHIFT_MEMBER_TRAVEL_ETA: 'pre_shift_eta',
  SESSION_PREP_TRAVEL: 'prep_travel',
  SESSION_LEAVE_NOW: 'leave_now',
  PROVIDER_EN_ROUTE: 'en_route',
  PROVIDER_NEARBY: 'nearby',
  PROVIDER_ARRIVED: 'arrived',
  SESSION_ARRIVED_CHECK_IN: 'arrived_check_in',
  SESSION_CHECKED_OUT: 'checked_out',
  // Telehealth (phone / video) start / end — no arrival or visit wording.
  SESSION_STARTED: 'session_started',
  SESSION_ENDED: 'session_ended',
  SESSION_CANCELLED: 'cancelled',
  SESSION_REMINDER_START: 'reminder_start',
  SESSION_PREP: 'reminder_prep',
  SESSION_REMINDER_END: 'reminder_end',
  SESSION_NOT_STARTED: 'not_started',
  CALLBACK_SESSION_BOOKED: 'callback_booked',
  // Ask a parent/guardian or support coordinator in their app (Premium Session Recording).
  SESSION_RECORDING_CONSENT_REQUEST: 'recording_consent_request',
}

/**
 * EVERY session push `data.type` (superset of the recorded ones: adds the
 * cancel-request push whose bell row is written elsewhere, and the legacy
 * per-stage journey pushes older backends still send). These all carry
 * first-name-only copy and must use Android lock-screen visibility `PRIVATE`.
 */
export const SESSION_PUSH_TYPES: readonly string[] = [
  ...Object.keys(SUBTYPE_BY_PUSH_TYPE),
  'SESSION_CANCEL_REQUEST',
  'PROVIDER_JOURNEY_STAGE',
  'PROVIDER_VERY_CLOSE',
]

/** True when `data.type` is any session-related push (recorded or not). */
export function isSessionPushType(type: string | null | undefined): boolean {
  return !!type && (SESSION_PUSH_TYPES.includes(type) || type.startsWith('SESSION_'))
}

/** Push `data.type`s that are recorded as history rows. */
export const SESSION_HISTORY_PUSH_TYPES: readonly string[] = Object.keys(SUBTYPE_BY_PUSH_TYPE)

/**
 * The history subtype for a push's `data`, or `null` when the push is not a
 * recordable session push (no known `type`, or no `sessionUuid`).
 */
export function sessionNotificationSubtype(
  data: Record<string, string> | undefined,
): SessionNotificationSubtype | null {
  if (!data) return null
  const base = SUBTYPE_BY_PUSH_TYPE[data.type ?? '']
  if (!base) return null
  if (!(data.sessionUuid ?? '').trim()) return null
  return base === 'arrived' && data.late === 'true' ? 'arrived_late' : base
}

/**
 * Which occurrence of `subtype` this push is, for idempotency. Most session
 * pushes fire at most once per session (their own fire-once guards), so a
 * retry of the same event collapses onto one row. Travel-plan pushes can
 * legitimately recur when the leave-by time changes, so they key on it.
 */
export function sessionNotificationOccurrence(
  subtype: SessionNotificationSubtype,
  data: Record<string, string> | undefined,
): string {
  if (subtype === 'pre_shift_eta' || subtype === 'prep_travel' || subtype === 'leave_now') {
    return (data?.leaveByAt ?? '').trim() || '1'
  }
  // Every consent request (a resend too) is its own row.
  if (subtype === 'recording_consent_request') return (data?.requestId ?? '').trim() || '1'
  return '1'
}

/** In-app route a history row opens — the session, not a specific panel. */
export function sessionNotificationPath(sessionUuid: string): string {
  return `/sessions?session=${encodeURIComponent(sessionUuid)}`
}

/** The consent request's accept screen (the person asked may not be able to open the session). */
export function recordingConsentRequestPath(sessionUuid: string, requestId: string): string {
  return `/recording-consent/${encodeURIComponent(sessionUuid)}/${encodeURIComponent(requestId)}`
}

/** Where a history row of `subtype` opens. */
export function sessionNotificationPathFor(
  subtype: SessionNotificationSubtype,
  sessionUuid: string,
  data: Record<string, string> | undefined,
): string {
  const requestId = (data?.requestId ?? '').trim()
  if (subtype === 'recording_consent_request' && requestId) return recordingConsentRequestPath(sessionUuid, requestId)
  return sessionNotificationPath(sessionUuid)
}

/**
 * Feed subtype for a notification row's `type` / `targetType`. New rows carry
 * `type: 'SESSION'` + the subtype in `targetType`; the pre-existing
 * cancel-request bell row has `type: 'SESSION_CANCEL_REQUEST'`. Returns `null`
 * when the row is not a session notification, `'other'` for a session row
 * whose subtype this client does not know (newer backend).
 */
export function subtypeForNotificationType(
  type: string | null | undefined,
  targetType?: string | null,
): SessionNotificationSubtype | 'other' | null {
  if (type === 'SESSION_CANCEL_REQUEST') return 'cancel_request'
  if (type !== SESSION_NOTIFICATION_TYPE) return null
  const known = new Set<string>([
    ...Object.values(SUBTYPE_BY_PUSH_TYPE),
    'arrived_late',
    'cancel_request',
  ])
  return targetType && known.has(targetType) ? (targetType as SessionNotificationSubtype) : 'other'
}

/** True for any notification row that belongs to the session history. */
export function isSessionNotificationType(type: string | null | undefined): boolean {
  return type === SESSION_NOTIFICATION_TYPE || type === 'SESSION_CANCEL_REQUEST'
}
